// React & Hooks
import { memo, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";

// API & Forms
import {
  createFleetWarehouseAPI,
  getFleetWarehouseAPI,
  updateFleetWarehouseAPI,
} from "services/api/fleet/warehouses/api";
import { initialFleetWarehouseEditForm } from "services/api/fleet/warehouses/consts";
import { useHandleTextFieldChange } from "services/forms/hooks";

// Types & Validation
import { FleetWarehouseEditFormValues } from "services/api/fleet/warehouses/types";
import { Errors } from "services/forms/validation/types";
import { WarehouseEditProps } from "./types";
import { WAREHOUSE_EDIT_FORM_SCHEMA } from "./validation";

// UI Komponente
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import Tabs from "ui/components/Tabs/Tabs";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

// Lokalne komponente
import WarehouseAddressTab from "./components/WarehouseAddressTab/WarehouseAddressTab";
import WarehouseContactTab from "./components/WarehouseContactTab/WarehouseContactTab";

const WarehouseEditCard: React.FC<WarehouseEditProps> = (props) => {
  const { warehouseId, show, onClose, onSuccess } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<FleetWarehouseEditFormValues>(
    initialFleetWarehouseEditForm
  );

  useEffect(() => {
    if (show && warehouseId) {
      fetchWarehouse(warehouseId);
    }
    if (show && !warehouseId) {
      setFormValues(initialFleetWarehouseEditForm);
      setErrors({});
    }
  }, [show, warehouseId]);

  const fetchWarehouse = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetWarehouseEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetWarehouseAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      WAREHOUSE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!warehouseId) {
        result = await updateFleetWarehouseAPI(warehouseId, formValues);
      } else {
        result = await createFleetWarehouseAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  return (
    <Modal show={show} onHide={onClose} position="centered">
      <ModalHeader icon="/media/images/add-icon.svg" title="Kreiranje skladišta" onHide={onClose} />
      <ModalBody overflow={false}>
        <div className="flex gap-4">
          <div className="w-full m-auto">
            <div className="space-y-3">
              <form noValidate className="text-left">
                <div className=" flex">
                  <div className="flex-1 px-5 pt-3 pb-5">
                    <div className="flex gap-3 mb-3">
                      <div className="flex-1">
                        <TextField
                          type="text"
                          name="name"
                          label={t("admin.fleet.warehouse.add.name")}
                          placeholder={t(
                            "admin.fleet.warehouse.add.name.placeholder"
                          )}
                          onChange={handleTextFieldChange}
                          error={errors.name}
                          value={formValues.name}
                          required
                          endIcon=""
                        ></TextField>
                      </div>
                    </div>
                    <Tabs
                      tabs={[
                        {
                          label: t("admin.fleet.warehouse.add.tab.address"),
                          content: (
                            <WarehouseAddressTab
                              formValues={formValues}
                              setFormValues={setFormValues}
                            />
                          ),
                        },
                        {
                          label: t("admin.fleet.warehouse.add.tab.contact"),
                          content: (
                            <WarehouseContactTab
                              formValues={formValues}
                              setFormValues={setFormValues}
                            />
                          ),
                        },
                      ]}
                    />
                  </div>
                  {/* <div className="border border-r-slate-200 bg-slate-50 h-screen p-4 w-1/3">
                    Details
                  </div> */}
                </div>
                <div className="px-5 py-3 absolute bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end ">
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    onClick={handleFormSubmit}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};
export default memo(WarehouseEditCard);
